import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Modal,
  Input,
  Table,
  Tabs,
  Tag,
  Spin,
  Card,
  Dropdown,
  Tooltip,
  Badge,
} from "antd";
import { useHistory, useLocation } from "react-router-dom";
import {
  FiSearch,
  FiRefreshCw,
  FiMoreVertical,
  FiDatabase,
} from "react-icons/fi";
import { RiEditFill, RiDeleteBin6Line } from "react-icons/ri";
import {
  MdOutlineExtension,
  MdPhoneAndroid,
  MdCategory,
  MdChat,
  MdDescription,
  MdRocketLaunch,
} from "react-icons/md";
import { HiTemplate, HiChatAlt2, HiCollection, HiDeviceMobile } from "react-icons/hi";

// Browser Extension APIs
import {
  getAllCopilotsPaginatedSearch,
  deleteCopilotById,
} from "@api/copilots/browser-extension/copilot/copilot";
import {
  getAllCategories,
  deleteCategoryById,
} from "@api/copilots/browser-extension/copilot/category";
import {
  getAllCopilotChatsPaginatedSearch,
  deleteCopilotChatById,
  seedDefaultCopilotChats,
  checkCopilotChatsSeedStatus,
} from "@api/copilots/browser-extension/chat/copilotChat";
import {
  getAllCopilotChatCategories,
  deleteCopilotChatCategoryById,
} from "@api/copilots/browser-extension/chat/copilotChatCategory";

// Mobile App APIs
import {
  getAllMobileCopilotChatsPaginatedSearch,
  deleteMobileCopilotChatById,
} from "@api/copilots/mobile-app/mobileCopilotChat";
import {
  getAllCategories as getAllMobileCopilotChatCategories,
  deleteMobileCopilotChatCategoryById,
} from "@api/copilots/mobile-app/mobileCopilotChatCategory";

// Components
import {
  SuccessNotification,
  ErrorNotification,
  CopilotCreateModal,
  CopilotUpdateModal,
  CopilotCategoryCreateModal,
  CopilotCategoryUpdateModal,
  CopilotChatCreateModal,
  CopilotChatUpdateModal,
  CopilotChatCategoryCreateModal,
  CopilotChatCategoryUpdateModal,
  MobileCopilotChatCreateModal,
  MobileCopilotChatUpdateModal,
  MobileCopilotChatCategoryCreateModal,
  MobileCopilotChatCategoryUpdateModal,
} from "@components";

import "@assets/css/AdminList.css";

const { TabPane } = Tabs;

// Animated number component
const AnimatedNumber = ({ value, duration = 1000 }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = value || 0;
    if (end === 0) {
      setDisplayValue(0);
      return;
    }
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setDisplayValue(end);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{displayValue.toLocaleString()}</span>;
};

const CopilotsDashboard = () => {
  const history = useHistory();
  const location = useLocation();

  // Get tab from URL or default
  const searchParams = new URLSearchParams(location.search);
  const initialPlatform = searchParams.get("platform") || "browser";
  const initialSection = searchParams.get("section") || "prompts";

  const [activePlatform, setActivePlatform] = useState(initialPlatform);
  const [activeSection, setActiveSection] = useState(initialSection);

  // Browser Extension State
  const [copilots, setCopilots] = useState([]);
  const [copilotChats, setCopilotChats] = useState([]);
  const [categories, setCategories] = useState([]);
  const [copilotChatCategories, setCopilotChatCategories] = useState([]);

  // Mobile App State
  const [mobileCopilotChats, setMobileCopilotChats] = useState([]);
  const [mobileCategories, setMobileCategories] = useState([]);

  // Loading States
  const [loading, setLoading] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);

  // Seed Status
  const [seedStatus, setSeedStatus] = useState({
    seeded: false,
    remaining: 0,
    totalTemplates: 0,
  });

  // Modal States
  const [modalState, setModalState] = useState({
    type: null,
    entity: null,
    id: null,
  });

  // Search and Pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Stats
  const [stats, setStats] = useState({
    totalCopilots: 0,
    totalCopilotChats: 0,
    totalCategories: 0,
    totalCopilotChatCategories: 0,
    totalMobileCopilotChats: 0,
    totalMobileCategories: 0,
  });

  // Update URL when tabs change
  useEffect(() => {
    const params = new URLSearchParams();
    params.set("platform", activePlatform);
    params.set("section", activeSection);
    history.replace({ search: params.toString() });
  }, [activePlatform, activeSection]);

  // Check seed status on mount
  useEffect(() => {
    fetchSeedStatus();
  }, []);

  const fetchSeedStatus = async () => {
    try {
      const { data } = await checkCopilotChatsSeedStatus();
      setSeedStatus({
        seeded: data.seeded,
        remaining: data.remaining,
        totalTemplates: data.totalTemplates,
        copilotCount: data.copilotCount,
        categoryCount: data.categoryCount,
      });
    } catch (error) {
      console.error("Error fetching seed status:", error);
    }
  };

  const handleSeedTemplates = async () => {
    Modal.confirm({
      title: "Seed Default Templates?",
      content: `This will create ${seedStatus.remaining} new chat assistants and their categories. Existing templates will be skipped.`,
      okText: "Seed Templates",
      cancelText: "Cancel",
      onOk: async () => {
        setSeedLoading(true);
        try {
          const { data } = await seedDefaultCopilotChats(false);
          SuccessNotification(
            `Seeded ${data.results.copilots.created} chat assistants and ${data.results.categories.created} categories successfully!`
          );
          fetchSeedStatus();
          fetchData();
        } catch (error) {
          ErrorNotification("Error seeding templates");
        } finally {
          setSeedLoading(false);
        }
      },
    });
  };

  // Fetch data based on active tab
  useEffect(() => {
    fetchData();
  }, [activePlatform, activeSection, pagination.current, pagination.pageSize]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activePlatform === "browser") {
        if (activeSection === "prompts") {
          await fetchCopilots();
        } else if (activeSection === "chats") {
          await fetchCopilotChats();
        } else if (activeSection === "categories") {
          await fetchCategories();
        } else if (activeSection === "chatCategories") {
          await fetchCopilotChatCategories();
        }
      } else if (activePlatform === "mobile") {
        if (activeSection === "chats") {
          await fetchMobileCopilotChats();
        } else if (activeSection === "categories") {
          await fetchMobileCategories();
        }
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Browser Extension Fetch Functions
  const fetchCopilots = async () => {
    const res = await getAllCopilotsPaginatedSearch(
      pagination.current,
      pagination.pageSize,
      searchTerm
    );
    setCopilots(res.data.copilots);
    setPagination((prev) => ({
      ...prev,
      total: res.data.paginationData.totalCopilots,
    }));
    setStats((prev) => ({
      ...prev,
      totalCopilots: res.data.paginationData.totalCopilots,
    }));
  };

  const fetchCopilotChats = async () => {
    const res = await getAllCopilotChatsPaginatedSearch(
      pagination.current,
      pagination.pageSize,
      searchTerm
    );
    setCopilotChats(res.data.copilotChats);
    setPagination((prev) => ({
      ...prev,
      total: res.data.paginationData.totalCopilotChats,
    }));
    setStats((prev) => ({
      ...prev,
      totalCopilotChats: res.data.paginationData.totalCopilotChats,
    }));
  };

  const fetchCategories = async () => {
    const { data } = await getAllCategories();
    setCategories(data);
    setStats((prev) => ({ ...prev, totalCategories: data.length }));
  };

  const fetchCopilotChatCategories = async () => {
    const { data } = await getAllCopilotChatCategories();
    setCopilotChatCategories(data);
    setStats((prev) => ({ ...prev, totalCopilotChatCategories: data.length }));
  };

  // Mobile App Fetch Functions
  const fetchMobileCopilotChats = async () => {
    const res = await getAllMobileCopilotChatsPaginatedSearch(
      pagination.current,
      pagination.pageSize,
      searchTerm
    );
    setMobileCopilotChats(res.data.mobileCopilotChats);
    setPagination((prev) => ({
      ...prev,
      total: res.data.paginationData.totalMobileCopilotChats,
    }));
    setStats((prev) => ({
      ...prev,
      totalMobileCopilotChats: res.data.paginationData.totalMobileCopilotChats,
    }));
  };

  const fetchMobileCategories = async () => {
    const { data } = await getAllMobileCopilotChatCategories();
    setMobileCategories(data);
    setStats((prev) => ({ ...prev, totalMobileCategories: data.length }));
  };

  // Search Handler
  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Pagination Handler
  const handleTableChange = (paginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));
  };

  // Modal Handlers
  const openModal = (type, entity, id = null) => {
    setModalState({ type, entity, id });
  };

  const closeModal = () => {
    setModalState({ type: null, entity: null, id: null });
  };

  // Delete Handlers
  const handleDelete = (entity, id, name) => {
    Modal.confirm({
      title: `Delete "${name}"?`,
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          switch (entity) {
            case "copilot":
              await deleteCopilotById(id);
              break;
            case "copilotChat":
              await deleteCopilotChatById(id);
              break;
            case "category":
              await deleteCategoryById(id);
              break;
            case "copilotChatCategory":
              await deleteCopilotChatCategoryById(id);
              break;
            case "mobileCopilotChat":
              await deleteMobileCopilotChatById(id);
              break;
            case "mobileCategory":
              await deleteMobileCopilotChatCategoryById(id);
              break;
          }
          SuccessNotification("Deleted successfully");
          fetchData();
        } catch (error) {
          ErrorNotification("Error deleting item");
        }
      },
    });
  };

  // Tab Change Handlers
  const handlePlatformChange = (key) => {
    setActivePlatform(key);
    setActiveSection(key === "browser" ? "prompts" : "chats");
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSearchTerm("");
  };

  const handleSectionChange = (key) => {
    setActiveSection(key);
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSearchTerm("");
  };

  // Get dropdown menu for actions
  const getDropdownMenu = (record, entity) => ({
    items: [
      {
        key: "edit",
        label: (
          <div className="admin-dropdown-item" onClick={() => openModal("update", entity, record._id)}>
            <RiEditFill size={15} />
            Edit
          </div>
        ),
      },
      { type: "divider" },
      {
        key: "delete",
        label: (
          <div className="admin-dropdown-item danger" onClick={() => handleDelete(entity, record._id, record.name)}>
            <RiDeleteBin6Line size={15} />
            Delete
          </div>
        ),
      },
    ],
  });

  // Column Definitions
  const copilotColumns = [
    { title: "#", dataIndex: "order", key: "order", width: 60 },
    {
      title: "Name",
      key: "name",
      width: 220,
      render: (_, record) => (
        <div className="admin-user-info">
          <div className="admin-user-avatar">
            {(record.name || "C").charAt(0).toUpperCase()}
          </div>
          <div className="admin-user-details">
            <div className="admin-user-name" style={{ cursor: "default" }}>
              {record.name}
            </div>
            <div className="admin-user-email">{record.description?.substring(0, 40) || "-"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (cat) => cat ? <Tag className="admin-plan-tag starter">{cat}</Tag> : <span style={{ color: "#94a3b8" }}>-</span>,
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      width: 140,
      render: (model) => <span style={{ fontSize: 12, color: "#64748b" }}>{model}</span>,
    },
    {
      title: "Inputs",
      dataIndex: "inputs",
      key: "inputs",
      width: 80,
      render: (inputs) => (
        <Tag className="admin-status-tag pending">{inputs?.length || 0}</Tag>
      ),
    },
    {
      title: "Usage",
      dataIndex: "used",
      key: "used",
      width: 80,
      render: (used) => <span style={{ fontWeight: 600 }}>{used?.toLocaleString() || 0}</span>,
    },
    {
      title: "Rating",
      key: "rating",
      width: 100,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Tag className="admin-status-tag active">{record.useful_generations?.length || 0}</Tag>
          <Tag className="admin-status-tag disabled">{record.poor_generations?.length || 0}</Tag>
        </div>
      ),
    },
    {
      title: "",
      key: "action",
      width: 50,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getDropdownMenu(record, "copilot")} trigger={["click"]} placement="bottomRight">
          <button className="admin-more-btn">
            <FiMoreVertical size={18} />
          </button>
        </Dropdown>
      ),
    },
  ];

  const copilotChatColumns = [
    { title: "#", dataIndex: "order", key: "order", width: 60 },
    {
      title: "Name",
      key: "name",
      width: 250,
      render: (_, record) => (
        <div className="admin-user-info">
          <div className="admin-user-avatar" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)" }}>
            {(record.name || "C").charAt(0).toUpperCase()}
          </div>
          <div className="admin-user-details">
            <div className="admin-user-name" style={{ cursor: "default" }}>
              {record.name}
            </div>
            <div className="admin-user-email">{record.description?.substring(0, 40) || "-"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      width: 150,
      render: (model) => <span style={{ fontSize: 12, color: "#64748b" }}>{model}</span>,
    },
    {
      title: "Usage",
      dataIndex: "used",
      key: "used",
      width: 80,
      render: (used) => <span style={{ fontWeight: 600 }}>{used?.toLocaleString() || 0}</span>,
    },
    {
      title: "Rating",
      key: "rating",
      width: 100,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Tag className="admin-status-tag active">{record.useful_generations?.length || 0}</Tag>
          <Tag className="admin-status-tag disabled">{record.poor_generations?.length || 0}</Tag>
        </div>
      ),
    },
    {
      title: "",
      key: "action",
      width: 50,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getDropdownMenu(record, "copilotChat")} trigger={["click"]} placement="bottomRight">
          <button className="admin-more-btn">
            <FiMoreVertical size={18} />
          </button>
        </Dropdown>
      ),
    },
  ];

  const categoryColumns = [
    {
      title: "Category Name",
      key: "name",
      render: (_, record) => (
        <div className="admin-user-info">
          <div className="admin-user-avatar" style={{ background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)" }}>
            {(record.name || "C").charAt(0).toUpperCase()}
          </div>
          <div className="admin-user-details">
            <div className="admin-user-name" style={{ cursor: "default" }}>
              {record.name}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Copilots",
      dataIndex: "copilots",
      key: "copilots",
      width: 120,
      render: (copilots) => (
        <Tag className="admin-plan-tag pro">{copilots?.length || 0} items</Tag>
      ),
    },
    {
      title: "",
      key: "action",
      width: 50,
      render: (_, record) => (
        <Dropdown menu={getDropdownMenu(record, "category")} trigger={["click"]} placement="bottomRight">
          <button className="admin-more-btn">
            <FiMoreVertical size={18} />
          </button>
        </Dropdown>
      ),
    },
  ];

  const copilotChatCategoryColumns = [
    { title: "#", dataIndex: "order", key: "order", width: 60 },
    {
      title: "Category",
      key: "name",
      render: (_, record) => (
        <div className="admin-user-info">
          <div
            className="admin-user-avatar"
            style={{
              background: record.color || "#6366f1",
              fontSize: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {record.icon || "📁"}
          </div>
          <div className="admin-user-details">
            <div className="admin-user-name" style={{ cursor: "default" }}>
              {record.name}
            </div>
            <div className="admin-user-email">{record.description?.substring(0, 40) || "-"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      width: 150,
      render: (slug) => <Tag color="blue">{slug}</Tag>,
    },
    {
      title: "Copilot Chats",
      dataIndex: "copilotChats",
      key: "copilotChats",
      width: 120,
      render: (chats) => (
        <Tag className="admin-plan-tag pro">{chats?.length || 0} items</Tag>
      ),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 80,
      render: (isActive) => (
        <Tag className={isActive ? "admin-status-tag active" : "admin-status-tag disabled"}>
          {isActive ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "",
      key: "action",
      width: 50,
      render: (_, record) => (
        <Dropdown menu={getDropdownMenu(record, "copilotChatCategory")} trigger={["click"]} placement="bottomRight">
          <button className="admin-more-btn">
            <FiMoreVertical size={18} />
          </button>
        </Dropdown>
      ),
    },
  ];

  const mobileCopilotChatColumns = [
    { title: "#", dataIndex: "order", key: "order", width: 60 },
    {
      title: "Name",
      key: "name",
      width: 250,
      render: (_, record) => (
        <div className="admin-user-info">
          <div className="admin-user-avatar" style={{ background: "linear-gradient(135deg, #ec4899 0%, #f472b6 100%)" }}>
            {(record.name || "M").charAt(0).toUpperCase()}
          </div>
          <div className="admin-user-details">
            <div className="admin-user-name" style={{ cursor: "default" }}>
              {record.name}
            </div>
            <div className="admin-user-email">{record.description?.substring(0, 40) || "-"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      width: 150,
      render: (model) => <span style={{ fontSize: 12, color: "#64748b" }}>{model}</span>,
    },
    {
      title: "Usage",
      dataIndex: "used",
      key: "used",
      width: 80,
      render: (used) => <span style={{ fontWeight: 600 }}>{used?.toLocaleString() || 0}</span>,
    },
    {
      title: "Rating",
      key: "rating",
      width: 100,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Tag className="admin-status-tag active">{record.useful_generations?.length || 0}</Tag>
          <Tag className="admin-status-tag disabled">{record.poor_generations?.length || 0}</Tag>
        </div>
      ),
    },
    {
      title: "",
      key: "action",
      width: 50,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getDropdownMenu(record, "mobileCopilotChat")} trigger={["click"]} placement="bottomRight">
          <button className="admin-more-btn">
            <FiMoreVertical size={18} />
          </button>
        </Dropdown>
      ),
    },
  ];

  const mobileCategoryColumns = [
    {
      title: "Category Name",
      key: "name",
      render: (_, record) => (
        <div className="admin-user-info">
          <div className="admin-user-avatar" style={{ background: "linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)" }}>
            {(record.name || "C").charAt(0).toUpperCase()}
          </div>
          <div className="admin-user-details">
            <div className="admin-user-name" style={{ cursor: "default" }}>
              {record.name}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Copilot Chats",
      dataIndex: "mobileCopilotChats",
      key: "mobileCopilotChats",
      width: 120,
      render: (chats) => (
        <Tag className="admin-plan-tag pro">{chats?.length || 0} items</Tag>
      ),
    },
    {
      title: "",
      key: "action",
      width: 50,
      render: (_, record) => (
        <Dropdown menu={getDropdownMenu(record, "mobileCategory")} trigger={["click"]} placement="bottomRight">
          <button className="admin-more-btn">
            <FiMoreVertical size={18} />
          </button>
        </Dropdown>
      ),
    },
  ];

  // Get current table config based on active tabs
  const getTableConfig = () => {
    if (activePlatform === "browser") {
      switch (activeSection) {
        case "prompts":
          return {
            columns: copilotColumns,
            data: copilots,
            createLabel: "Create Prompt Template",
            entity: "copilot",
          };
        case "chats":
          return {
            columns: copilotChatColumns,
            data: copilotChats,
            createLabel: "Create Chat Assistant",
            entity: "copilotChat",
          };
        case "categories":
          return {
            columns: categoryColumns,
            data: categories,
            createLabel: "Create Category",
            entity: "category",
            noPagination: true,
          };
        case "chatCategories":
          return {
            columns: copilotChatCategoryColumns,
            data: copilotChatCategories,
            createLabel: "Create Chat Category",
            entity: "copilotChatCategory",
            noPagination: true,
          };
        default:
          return { columns: [], data: [] };
      }
    } else {
      switch (activeSection) {
        case "chats":
          return {
            columns: mobileCopilotChatColumns,
            data: mobileCopilotChats,
            createLabel: "Create Chat Assistant",
            entity: "mobileCopilotChat",
          };
        case "categories":
          return {
            columns: mobileCategoryColumns,
            data: mobileCategories,
            createLabel: "Create Category",
            entity: "mobileCategory",
            noPagination: true,
          };
        default:
          return { columns: [], data: [] };
      }
    }
  };

  const tableConfig = getTableConfig();

  // Get stats for current view
  const getCurrentStats = () => {
    if (activePlatform === "browser") {
      return [
        { label: "Prompt Templates", value: stats.totalCopilots, icon: <HiTemplate />, color: "purple" },
        { label: "Chat Assistants", value: stats.totalCopilotChats, icon: <HiChatAlt2 />, color: "green" },
        { label: "Prompt Categories", value: stats.totalCategories, icon: <HiCollection />, color: "blue" },
        { label: "Chat Categories", value: stats.totalCopilotChatCategories, icon: <HiCollection />, color: "cyan" },
      ];
    } else {
      return [
        { label: "Chat Assistants", value: stats.totalMobileCopilotChats, icon: <HiDeviceMobile />, color: "purple" },
        { label: "Categories", value: stats.totalMobileCategories, icon: <HiCollection />, color: "blue" },
      ];
    }
  };

  // Render Modals
  const renderModals = () => {
    const { type, entity, id } = modalState;
    if (!type) return null;

    const modalProps = {
      open: true,
      handleModalClick: closeModal,
    };

    if (type === "create") {
      switch (entity) {
        case "copilot":
          return <CopilotCreateModal {...modalProps} fetchCopilots={fetchCopilots} />;
        case "copilotChat":
          return <CopilotChatCreateModal {...modalProps} fetchCopilotChats={fetchCopilotChats} />;
        case "category":
          return <CopilotCategoryCreateModal {...modalProps} fetchCategories={fetchCategories} />;
        case "copilotChatCategory":
          return <CopilotChatCategoryCreateModal {...modalProps} fetchCategories={fetchCopilotChatCategories} />;
        case "mobileCopilotChat":
          return <MobileCopilotChatCreateModal {...modalProps} fetchMobileCopilotChats={fetchMobileCopilotChats} />;
        case "mobileCategory":
          return <MobileCopilotChatCategoryCreateModal {...modalProps} fetchMobileCopilotChatCategories={fetchMobileCategories} />;
      }
    } else if (type === "update") {
      switch (entity) {
        case "copilot":
          return <CopilotUpdateModal {...modalProps} fetchCopilots={fetchCopilots} copilotId={id} />;
        case "copilotChat":
          return <CopilotChatUpdateModal {...modalProps} fetchCopilotChats={fetchCopilotChats} copilotChatId={id} />;
        case "category":
          return <CopilotCategoryUpdateModal {...modalProps} fetchCategories={fetchCategories} categoryId={id} />;
        case "copilotChatCategory":
          return <CopilotChatCategoryUpdateModal {...modalProps} fetchCategories={fetchCopilotChatCategories} categoryId={id} />;
        case "mobileCopilotChat":
          return <MobileCopilotChatUpdateModal {...modalProps} fetchMobileCopilotChats={fetchMobileCopilotChats} mobileCopilotChatId={id} />;
        case "mobileCategory":
          return <MobileCopilotChatCategoryUpdateModal {...modalProps} fetchMobileCopilotChatCategories={fetchMobileCategories} mobileCopilotChatCategoryId={id} />;
      }
    }
    return null;
  };

  const currentStats = getCurrentStats();

  return (
    <div className="admin-list-container">
      {renderModals()}

      {/* Header */}
      <div className="admin-list-header">
        <div>
          <h1>Copilots</h1>
          <p>Manage prompt templates and chat assistants for browser extension and mobile app</p>
        </div>
        <div className="admin-list-actions">
          {activePlatform === "browser" && seedStatus.remaining > 0 && (
            <Tooltip title={`Seed ${seedStatus.remaining} default chat assistant templates`}>
              <Badge count={seedStatus.remaining} overflowCount={99}>
                <Button
                  className="admin-btn-secondary"
                  icon={<FiDatabase size={16} />}
                  onClick={handleSeedTemplates}
                  loading={seedLoading}
                >
                  Seed Templates
                </Button>
              </Badge>
            </Tooltip>
          )}
          <Button
            className="admin-btn-secondary"
            icon={<FiRefreshCw size={16} />}
            onClick={() => fetchData()}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[20, 20]} className="admin-stats-row">
        {currentStats.map((stat, index) => (
          <Col xs={24} sm={12} lg={24 / currentStats.length} key={index}>
            <Card className="admin-stat-card">
              <div className={`admin-stat-icon ${stat.color}`}>
                {stat.icon}
              </div>
              <div className="admin-stat-label">{stat.label}</div>
              <div className={`admin-stat-value ${stat.color}`}>
                <AnimatedNumber value={stat.value} />
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Platform Tabs */}
      <Card className="admin-table-card" style={{ marginBottom: 16 }}>
        <Tabs
          activeKey={activePlatform}
          onChange={handlePlatformChange}
          style={{ marginBottom: 0 }}
          tabBarStyle={{ margin: 0, padding: "0 16px" }}
        >
          <TabPane
            tab={
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <MdOutlineExtension size={18} />
                Browser Extension
              </span>
            }
            key="browser"
          />
          <TabPane
            tab={
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <MdPhoneAndroid size={18} />
                Mobile App
              </span>
            }
            key="mobile"
          />
        </Tabs>
      </Card>

      {/* Section Tabs + Action Bar */}
      <div className="admin-action-bar">
        <Tabs
          activeKey={activeSection}
          onChange={handleSectionChange}
          style={{ marginBottom: 0 }}
          size="small"
        >
          {activePlatform === "browser" && (
            <>
              <TabPane
                tab={
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <MdDescription size={16} />
                    Prompt Templates
                  </span>
                }
                key="prompts"
              />
              <TabPane
                tab={
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <MdChat size={16} />
                    Chat Assistants
                  </span>
                }
                key="chats"
              />
              <TabPane
                tab={
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <MdCategory size={16} />
                    Prompt Categories
                  </span>
                }
                key="categories"
              />
              <TabPane
                tab={
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <HiCollection size={16} />
                    Chat Categories
                  </span>
                }
                key="chatCategories"
              />
            </>
          )}
          {activePlatform === "mobile" && (
            <>
              <TabPane
                tab={
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <MdChat size={16} />
                    Chat Assistants
                  </span>
                }
                key="chats"
              />
              <TabPane
                tab={
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <MdCategory size={16} />
                    Categories
                  </span>
                }
                key="categories"
              />
            </>
          )}
        </Tabs>
      </div>

      {/* Search + Create Bar */}
      <div className="admin-action-bar" style={{ marginTop: 16 }}>
        {activeSection !== "categories" && activeSection !== "chatCategories" && (
          <div className="admin-search-wrapper">
            <Input
              className="admin-search-input"
              placeholder="Search by name..."
              prefix={<FiSearch size={16} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPressEnter={() => fetchData()}
              allowClear
              onClear={() => {
                setSearchTerm("");
                fetchData();
              }}
            />
          </div>
        )}
        <div className="admin-list-actions">
          <Button
            className="admin-btn-primary"
            icon={<MdRocketLaunch size={16} />}
            onClick={() => openModal("create", tableConfig.entity)}
          >
            {tableConfig.createLabel}
          </Button>
        </div>
      </div>

      {/* Data Table */}
      <Card className="admin-table-card">
        <Table
          className="admin-table"
          columns={tableConfig.columns}
          dataSource={tableConfig.data}
          rowKey={(record) => record._id}
          pagination={
            tableConfig.noPagination
              ? false
              : {
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                }
          }
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default CopilotsDashboard;
